import {
  AccountBoxOutlinedIcon,
  BuildOutlinedIcon,
  CalendarTodayOutlinedIcon,
  DescriptionOutlinedIcon,
  ExitToAppOutlinedIcon,
  HelpOutlineOutlinedIcon,
  PeopleOutlinedIcon,
  PersonOutlineIcon,
  SettingsOutlinedIcon,
  TimesheetIcon,
  UserManagementIcon,
} from 'element';
import { useRoute, useSelectorWithProps } from 'hooks';
import AuthService from 'lib/AuthService';
import { compact } from 'lodash';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { privateRoutes } from 'routes';
import {
  getCanEditProfile,
  getEmployeeCanEnablePunchClock,
} from 'state/Account';
import { BOX_ACCOUNTS_ADMIN_LOGIN_REQUEST } from 'state/Accounts';
import { getFlag } from 'state/Flags';
import {
  BOX_MAIN_MENU_HELP_ITEM_CONFIRMATION_OPENED,
  BOX_MAIN_MENU_TIMECLOCK_ITEM_CONFIRMATION_OPENED,
} from 'state/MainMenuHelpItem';
import {
  usePermissionsFilter,
  useRosterMenuItem,
  useUpdateSelected,
} from './hooks';
import { hasPermissionSelector } from 'state/Auth';
import { CampaignOutlined, EmojiObjectsOutlined } from '@mui/icons-material';

export const useRosterMenu = () => {
  const dispatch = useDispatch();
  const route = useRoute();
  const rosterMenuItem = useRosterMenuItem();
  const permissionsFilter = usePermissionsFilter();
  const updateSelected = useUpdateSelected();

  const isEmployeeCanActivate = useSelector(getEmployeeCanEnablePunchClock);
  const hasManagerDashboardPermission = useSelectorWithProps(
    hasPermissionSelector,
    'managerdashboard'
  );

  const canEditProfile = useSelector(getCanEditProfile);
  const isWhatsNewEnabled = useSelector(getFlag.whatsNew);

  const allMenuItems = {
    managerDashboard: {
      id: 'managerDashboard',
      title: 'Manager Dashboard',
      url: privateRoutes.managerDashboard.path,
      icon: UserManagementIcon,
      handleClick: () => route.goTo(privateRoutes.managerDashboard.path),
      permission: (privateRoutes.managerDashboard as any).permission,
      testId: 'rosterMenu_items_managerDashboard',
    },
    employeeDashboard: {
      id: 'employeeDashboard',
      title: 'Employee Dashboard',
      url: privateRoutes.employeeDashboard.path,
      icon: PersonOutlineIcon,
      handleClick: () => route.goTo(privateRoutes.employeeDashboard.path),
      permission: (privateRoutes.employeeDashboard as any).permission,
      testId: 'rosterMenu_items_employeeDashboard',
    },
    roster: {
      ...rosterMenuItem,
      id: 'roster',
      icon: CalendarTodayOutlinedIcon,
      permission: (privateRoutes.roster as any).permission,
      testId: 'rosterMenu_items_roster',
    },
    timesheets: {
      id: 'timesheets',
      title: 'Timesheets - Daily',
      url: privateRoutes.timesheets.path,
      icon: TimesheetIcon,
      handleClick: () => route.goTo(privateRoutes.timesheets.path),
      permission: (privateRoutes.timesheets as any).permission,
      testId: 'rosterMenu_items_timesheets',
    },
    newTimesheets: {
      id: 'newTimesheets',
      title: 'Timesheets - Weekly',
      url: privateRoutes.timesheetsWeekly.path,
      icon: TimesheetIcon,
      handleClick: () => route.goTo(privateRoutes.timesheetsWeekly.path),
      permission: (privateRoutes.timesheetsWeekly as any).permission,
      testId: 'rosterMenu_items_timeseheetsWeekly',
    },
    activateTimeClock: {
      id: 'activateTimeClock',
      title: 'Activate TimeClock',
      url: process.env.REACT_APP_PUNCHCLOCK_URL as string,
      icon: TimesheetIcon,
      handleClick: () => {
        dispatch(BOX_MAIN_MENU_TIMECLOCK_ITEM_CONFIRMATION_OPENED());
      },
      permission: undefined,
      testId: 'rosterMenu_items_timeclock',
    },
    users: {
      id: 'users',
      title: 'People',
      url: privateRoutes.users.path,
      icon: PeopleOutlinedIcon,
      handleClick: () => route.goTo(privateRoutes.users.path),
      permission: (privateRoutes.users as any).permission,
      testId: 'rosterMenu_items_users',
    },
    reports: {
      id: 'reports',
      title: 'Reports',
      url: privateRoutes.reports.path,
      icon: DescriptionOutlinedIcon,
      handleClick: () => route.goTo(privateRoutes.reports.path),
      permission: (privateRoutes.reports as any).permission,
      testId: 'rosterMenu_items_reports',
    },
    preferences: {
      id: 'preferences',
      title: 'Preferences',
      url: privateRoutes.preferences.path,
      icon: BuildOutlinedIcon,
      handleClick: () => route.goTo(privateRoutes.preferences.path),
      permission: (privateRoutes.preferences as any).permission,
      testId: 'rosterMenu_items_preferences',
    },
    settings: {
      id: 'settings',
      title: 'Settings',
      url: privateRoutes.settings.path,
      icon: SettingsOutlinedIcon,
      handleClick: () => route.goTo(privateRoutes.settings.path),
      permission: (privateRoutes.settings as any).permission,
      testId: 'rosterMenu_items_settings',
    },
    accounts: {
      id: 'accounts',
      title: 'Accounts',
      url: privateRoutes.accounts.path,
      icon: AccountBoxOutlinedIcon,
      handleClick: () => route.goTo(privateRoutes.accounts.path),
      permission: (privateRoutes.accounts as any).permission,
      testId: 'rosterMenu_items_accounts',
    },
    help: {
      id: 'help',
      title: 'Help',
      url: process.env.REACT_APP_HELP_URL as string,
      icon: HelpOutlineOutlinedIcon,
      handleClick: () => {
        dispatch(BOX_MAIN_MENU_HELP_ITEM_CONFIRMATION_OPENED());
      },
      permission: undefined,
      testId: 'rosterMenu_items_help',
    },
    whatsNew: {
      id: 'upcoming-ideas',
      title: `Submit an idea`,
      url: '#',
      icon: EmojiObjectsOutlined,
      handleClick: () => {
        window.open(process.env.REACT_APP_WHATS_NEW_URL as string);
      },
      permission: (privateRoutes.managerDashboard as any).permission,
      testId: 'rosterMenu_items_upcoming_ideas',
    },
    superAdminLogin: {
      id: 'superAdminLogin',
      title: 'Login as a Super Admin',
      url: privateRoutes.accounts.path,
      icon: ExitToAppOutlinedIcon,
      handleClick: () => {
        dispatch(BOX_ACCOUNTS_ADMIN_LOGIN_REQUEST());
      },
      permission: undefined,
      testId: 'rosterMenu_items_superAdminLogin',
    },
  };

  const menuItemsArray = compact([
    allMenuItems.managerDashboard,
    allMenuItems.employeeDashboard,
    allMenuItems.roster,
    allMenuItems.timesheets,
    allMenuItems.newTimesheets,
    (hasManagerDashboardPermission || isEmployeeCanActivate) &&
      allMenuItems.activateTimeClock,
    allMenuItems.users,
    allMenuItems.reports,
    canEditProfile && allMenuItems.preferences,
    allMenuItems.settings,
    allMenuItems.accounts,
    allMenuItems.help,
    isWhatsNewEnabled &&
      process.env.REACT_APP_WHATS_NEW_URL &&
      allMenuItems.whatsNew,
    AuthService.isImpersonated() && allMenuItems.superAdminLogin,
  ]);

  const itemsFilteredByPermission = permissionsFilter(menuItemsArray);
  return updateSelected(itemsFilteredByPermission);
};
